import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Send, FileDown } from "lucide-react";
import { fileUrl } from "@/lib/uploads";
import { formatSlaLabel, slaToneClass } from "./slaHelpers";

const PRIORITY_TONE = {
  high:   "border-red-500/40 text-red-400 bg-red-500/5",
  medium: "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5",
  low:    "border-gray-500/40 text-gray-400 bg-gray-500/5",
};
const STATUS_TONE = {
  open:        "border-red-500/40 text-red-400 bg-red-500/5",
  in_progress: "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5",
  resolved:    "border-emerald-500/40 text-emerald-400 bg-emerald-500/5",
};
const STATUS_ORDER = ["open", "in_progress", "resolved"];

export default function TicketDetailModal({ ticketId, currentUserId, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      setT(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load ticket");
      onClose?.();
    }
  }, [ticketId, onClose]);

  useEffect(() => { load(); }, [load]);

  const userName = (uid) => {
    const u = (t?.users || []).find((x) => x.id === uid);
    return u?.name || "—";
  };
  const userRole = (uid) => {
    const u = (t?.users || []).find((x) => x.id === uid);
    return u?.role_name || "";
  };

  const canChangeStatus = t && (
    t.created_by === currentUserId
    || (t.escalated_manager_ids || []).includes(currentUserId)
  );

  const changeStatus = async (newStatus) => {
    if (!t || t.status === newStatus) return;
    setBusy(true);
    try {
      await api.patch(`/tickets/${t.id}/status`, { status: newStatus });
      toast.success(`Marked ${newStatus.replace(/_/g, " ")}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${t.id}/comments`, { body: comment.trim() });
      setComment("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Comment failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="ticket-detail-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
              Ticket
            </p>
            <h3 className="font-display text-xl text-white mt-0.5 truncate" data-testid="ticket-detail-title">
              {t?.title || "…"}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="ticket-detail-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {!t ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading…
          </div>
        ) : (
          <div className="overflow-y-auto p-5 space-y-5">
            {/* Header meta */}
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${STATUS_TONE[t.status] || ""}`}
                    data-testid="ticket-status-pill">
                {t.status?.replace(/_/g, " ")}
              </span>
              <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${PRIORITY_TONE[t.priority] || ""}`}>
                {t.priority} priority
              </span>
              {formatSlaLabel(t) && (
                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${slaToneClass(t)}`}
                      title={t.sla_deadline_at ? `SLA target: ${new Date(t.sla_deadline_at).toLocaleString()}` : ""}
                      data-testid="ticket-sla-chip">
                  SLA · {formatSlaLabel(t)}
                </span>
              )}
              <span className="text-[10px] text-gray-500">
                Raised by <span className="text-white">{t.created_by_name || "—"}</span>
                {" · "}
                {new Date(t.created_at).toLocaleString()}
              </span>
            </div>

            {/* Status transitions */}
            {canChangeStatus && (
              <div className="iz-card p-3">
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
                  Update status
                </p>
                <div className="flex gap-2 flex-wrap" data-testid="ticket-status-controls">
                  {STATUS_ORDER.map((s) => (
                    <button key={s} onClick={() => changeStatus(s)}
                            disabled={busy || t.status === s}
                            className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border transition-colors disabled:opacity-40 ${
                              t.status === s
                                ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
                                : "border-[#262626] text-gray-400 hover:text-white hover:border-[#333]"
                            }`}
                            data-testid={`ticket-set-${s}`}>
                      {s.replace(/_/g, " ")}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {t.project?.client_name && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Project</p>
                <p className="text-sm text-white">
                  {t.project.client_name}
                  {t.project.project_name ? ` · ${t.project.project_name}` : ""}
                </p>
              </div>
            )}

            {t.description && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Description</p>
                <p className="text-sm text-white whitespace-pre-wrap" data-testid="ticket-detail-desc">
                  {t.description}
                </p>
              </div>
            )}

            {/* Tagged users */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
                Tagged ({t.tagged_user_ids?.length || 0})
              </p>
              {t.tagged_user_ids?.length ? (
                <div className="flex flex-wrap gap-1.5" data-testid="ticket-detail-tagged">
                  {t.tagged_user_ids.map((uid) => (
                    <span key={uid} className="text-[11px] border border-[#262626] rounded-full px-2 py-0.5 text-white">
                      {userName(uid)}
                      {userRole(uid) ? <span className="text-gray-500"> · {userRole(uid)}</span> : null}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic">None</p>
              )}
            </div>

            {/* Escalated managers */}
            {t.escalated_manager_ids?.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
                  Escalated to ({t.escalated_manager_ids.length})
                </p>
                <div className="flex flex-wrap gap-1.5" data-testid="ticket-detail-escalated">
                  {t.escalated_manager_ids.map((uid) => (
                    <span key={uid} className="text-[11px] border border-[#d4af37]/40 bg-[#d4af37]/5 text-[#d4af37] rounded-full px-2 py-0.5">
                      {userName(uid)}
                      {userRole(uid) ? <span className="text-[#d4af37]/70"> · {userRole(uid)}</span> : null}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            {t.attachment_file_ids?.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
                  Attachments ({t.attachment_file_ids.length})
                </p>
                <ul className="space-y-1" data-testid="ticket-detail-attachments">
                  {t.attachment_file_ids.map((fid, i) => (
                    <li key={fid}>
                      <a href={fileUrl(fid)} target="_blank" rel="noreferrer"
                         className="text-xs text-[#d4af37] hover:underline inline-flex items-center gap-1"
                         data-testid={`ticket-attach-dl-${i}`}>
                        <FileDown className="w-3.5 h-3.5"/> Attachment #{i + 1}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Comments */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
                Comments ({t.comments?.length || 0})
              </p>
              <div className="space-y-2 mb-3" data-testid="ticket-comments-list">
                {(t.comments || []).length === 0 && (
                  <p className="text-xs text-gray-600 italic">No comments yet.</p>
                )}
                {(t.comments || []).map((c) => (
                  <div key={c.id} className="border border-[#1a1a1a] rounded-md p-3"
                       data-testid={`ticket-comment-${c.id}`}>
                    <p className="text-[10px] text-gray-500 mb-1">
                      <span className="text-white">{c.by_user_name || "—"}</span> ·{" "}
                      {new Date(c.at).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-300 whitespace-pre-wrap">{c.body}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={comment} onChange={(e) => setComment(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                       placeholder="Add a comment…"
                       className="iz-input flex-1 text-xs"
                       data-testid="ticket-comment-input"/>
                <button onClick={submitComment} disabled={busy || !comment.trim()}
                        className="iz-btn-primary text-xs flex items-center gap-1.5"
                        data-testid="ticket-comment-submit">
                  <Send className="w-3.5 h-3.5"/> Post
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
